<template>
  <section>
    <div class="miniature-session container">
      <div class="miniature-header row">
        <div class="col-sm d-flex align-items-center font-weight-bolder">
          {{ getSurnameWithInitials(training.patient) }}
        </div>
        <div class="d-flex align-items-center ml-2">
          <span class="connection-info" v-if="!this.connect">{{
            $t('src/components/table-rows/trainingstablerow.offline')
          }}</span>
          <svg-icon
            :name="nameIconConnect"
            class="svg-icon-without-hover mr-3"
            :height="22"
            :width="22"
          ></svg-icon>
          <svg-icon :name="'device'" class="svg-icon-without-hover" :height="18" :width="18"></svg-icon>
          <svg-icon :name="getDeviceCharge" class="svg-icon-without-hover mr-2"></svg-icon>
          <svg-icon :name="'smartphone'" class="svg-icon-without-hover" :height="18" :width="18"></svg-icon>
          <svg-icon :name="getPhoneCharge" class="svg-icon-without-hover mr-2"></svg-icon>
        </div>

        <svg-icon
          :name="'expand'"
          class="svg-icon ml-3 mr-2"
          @click:icon="goToTraining"
          :height="27"
          :width="27"
        ></svg-icon>
      </div>

      <div class="row">
        <div class="col-sm-auto miniature-col-sm">
          <div style="padding-left: 10px; padding-right: 10px;">
            <div class="d-flex align-items-center ml-1">
              <svg-icon
                :name="'heart'"
                class="svg-icon-without-hover mt-2 mr-1"
                :width="22"
                :height="22"
              ></svg-icon>
              <div class="current-hr" :class="hasRangeHR ? colorHR : 'current-hr-default'">
                {{ currentHR }}
              </div>
            </div>
            <input
              :id="`rangeHR-${trainingId}`"
              class="position-absolute"
              type="range"
              min="0"
              max="200"
              disabled
            />

            <div class="session-info">
              <div class="d-flex align-items-center mb-2">
                <svg-icon :name="'steps'" class="svg-icon-without-hover" :height="16" :width="16"></svg-icon>
                <div class="cell-text">
                  {{ currentSR }}
                </div>
                <div class="cell-units pl-1">{{ $t('unit.sm-short') }}</div>
              </div>

              <div class="d-flex align-items-center">
                <svg-icon :name="'breath'" class="svg-icon-without-hover" :height="16" :width="16"></svg-icon>
                <div class="cell-text">
                  {{ currentBF }}
                </div>
                <div class="cell-units pl-1">{{ $t('unit.rrm') }}</div>
              </div>
            </div>
          </div>
          <div class="canvas"></div>

          <div class="event-container">
            <template v-if="events && events.length > 0">
              <div
                class="col-6 mb-1 event-element"
                v-for="(ev, index) in lastTwoEvents"
                :key="`event-${trainingId}-${index}`"
              >
                <span
                  class="badge badge-fix-s badge-pill mr-2"
                  :class="ev.color ? 'badge-' + ev.color : 'badge-danger'"
                  v-html="ev.abbr"
                >
                </span
                >{{ ev.title }}
                <span>{{ ev.time }}</span>
              </div>
            </template>
          </div>
        </div>

        <div class="col-sm chart-container">
          <training-miniature-chart
            :key="componentKey"
            v-show="this.graphView === 'hrGraph'"
            v-if="loadingRhytm"
            :rhythmogram="rhythmogram"
            :maxHR="training.heart_rate_max"
            :minHR="training.heart_rate_min"
            :trainingDuration="training.duration"
          ></training-miniature-chart>
          <active-training-miniature-ecg
            v-if="this.graphView === 'ecgGraph'"
            :key="componentKey"
            :trainingId="training.id"
            @reload:ecg="reloadEcg"
          >
          </active-training-miniature-ecg>

          <div
            :id="`myProgress-${trainingId}`"
            class="miniature-progress-bar"
            :class="{ 'for-ecg': this.graphView === 'ecgGraph' }"
          >
            <canvas :id="`event-${trainingId}`" class="canvas-event"></canvas>
            <div class="duration-progress">{{ trainingDurationStr }} {{ trainingSetDurationStr }}</div>
            <div :id="`myBar-${trainingId}`" class="progress-line"></div>
          </div>

          <canvas :id="`tip-${trainingId}`" height="18" width="150" class="canvas-tip p-0"></canvas>

          <div :id="`eventTooltip-${trainingId}`" class="miniature-event-tooltip">
            <div class="badge text-event-abbr" :class="'badge-' + colorEvent" v-html="textEventAbbr"></div>
            <div class="text-event-time">
              {{ textEventTime }}<span class="font-weight-bolder">{{ textEventValue }}</span>
            </div>
          </div>

          <div class="btn-group">
            <button
              type="button"
              class="btn btn-outline-none btn-with-svg-icon-small"
              :class="graphView === 'ecgGraph' ? 'btn-secondary' : 'btn-outline-secondary'"
              @click="setViewReport('ecgGraph')"
              :title="$t('title.ecgGraph')"
            >
              <svg-icon class="svg-icon" :name="'ecg-graph'" :height="19" :width="19"></svg-icon>
            </button>
            <button
              type="button"
              class="btn btn-outline-none btn-with-svg-icon-small"
              :class="graphView === 'hrGraph' ? 'btn-secondary' : 'btn-outline-secondary'"
              @click="setViewReport('hrGraph')"
              :title="$t('title.hrGraph')"
            >
              <svg-icon class="svg-icon" :name="'heart-dark'" :height="16" :width="16"></svg-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { eventsConfig } from '@/events.config.js'
import http from '@/modules/http.js'
import { fullName } from '@/mixins/full-name.js'
import { subscribeMobileEvents } from '@/mixins/subscribe-mobile-events.js'
import { eventTypes } from '@/main.config.js'
import timeString from '@/modules/time-string.js'
import { trainingDate } from '@/mixins/training-date.js'
import '@/styles/elements/badges.less'
import '@/styles/components/range-slider.less'
import { userRights } from '@/mixins/user-rights.js'

const DEFAULT_DURATION_MIN = 20

export default {
  name: 'ActiveTrainingMiniature',
  components: {
    SvgIcon: () => import('@/components/SvgIcon.vue'),
    TrainingMiniatureChart: () => import('@/components/Training/TrainingMiniatureChart.vue'),
    ActiveTrainingMiniatureEcg: () => import('@/components/Training/ActiveTrainingMiniatureEcg.vue')
  },
  mixins: [fullName, subscribeMobileEvents, trainingDate, userRights],
  props: {
    training: {
      type: Object,
      required: true
    },
    displayMode: {
      type: String,
      required: true
    },
    eventsInfoView: {
      type: Boolean,
      required: true
    }
  },
  data: () => ({
    colorEvent: '#ff5722',
    componentKey: 0,
    connect: true,
    currentHR: '-',
    currentBF: '-',
    currentSR: '-',
    currentPhoneCharge: null,
    currentDeviceCharge: null,
    currentDuration: null,
    childKey: 0,
    duration: DEFAULT_DURATION_MIN,
    events: null,
    eventsBeginIndexes: {},
    eventsForTooltip: [],
    graphView: 'hrGraph',
    lastDataTime: null,
    load: true,
    loadingRhytm: null,
    mouseX: null,
    now: null,
    rhythmogram: null,
    timer: null,
    textEventTime: '',
    textEventAbbr: '',
    textEventValue: ''
  }),
  computed: {
    hasRangeHR() {
      if (this.training.heart_rate_min && this.training.heart_rate_max) {
        return true
      } else return false
    },
    colorHR() {
      if (this.currentHR > this.training.heart_rate_max) return 'current-hr-max'
      else if (this.currentHR < this.training.heart_rate_min) return 'current-hr-min'
      else return 'current-hr-default'
    },
    trainingSetDurationStr() {
      if (this.training.duration) return `/ ${timeString(Math.round(this.training.duration * 60), true)}`
      else return ''
    },
    trainingDurationStr() {
      return timeString(Math.round(this.currentDuration), true)
    },
    trainingStartDate() {
      return this.getTrainingMomentStartDate(this.training)
    },
    trainingId() {
      return this.training.id
    },
    nameIconConnect() {
      return this.connect ? 'connection' : 'connection_off'
    },
    lastTwoEvents() {
      return this.events.slice(-2)
    },
    getDeviceCharge() {
      return this.getCharge(this.currentDeviceCharge)
    },
    getPhoneCharge() {
      return this.getCharge(this.currentPhoneCharge)
    }
  },
  watch: {
    now() {
      this.changeSessionDurationForProgressBar()
      this.updateCurrentPositionOnProgressBar()
      this.checkConnectionStatus()
    },
    displayMode() {
      this.componentKey += 1
      this.eventCanvas.width = document.getElementById(`myProgress-${this.trainingId}`).clientWidth
      this.redrawEventsOnProgressBar()
    },
    eventsInfoView() {
      this.componentKey += 1
      this.eventCanvas.width = document.getElementById(`myProgress-${this.trainingId}`).clientWidth
      this.redrawEventsOnProgressBar()
    }
  },
  mounted() {
    this.componentKey = this.training.id
    this.canSubscribe = true
    this.now = this.moment().local()
    this.getPreviousData()
    this.createAndSettingCanvases()
    this.setSessionDurationForProgressBar()
    this.updateNow()
  },
  destroyed() {
    clearInterval(this.timer)
  },
  methods: {
    setSessionDurationForProgressBar() {
      if (this.training.duration) {
        this.duration = this.training.duration
      }
      let currentDurationMinutes = this.now.diff(this.trainingStartDate, 'minutes')
      if (currentDurationMinutes > this.duration) {
        this.duration = currentDurationMinutes + DEFAULT_DURATION_MIN
      }
    },
    updateNow() {
      this.timer = setInterval(() => {
        this.now = this.moment().local()
      }, 1000)
    },
    getPreviousData() {
      this.load = true
      this.events = []
      Promise.all([
        this.getPreviousRhytmogram(),
        this.getPreviousEvents(),
        this.getPreviousBreath(),
        this.getPreviousSteps(),
        this.getPreviousChargeInfo()
      ])
        .then(responses => {
          this.rhythmogram = responses[0]
          if (responses[1].total > 0) {
            responses[1].data.forEach(event => {
              if (event.type === eventTypes.cardioEvent) {
                this.eventProcessing(event)
              }
            })
          }
          this.loadingRhytm = true

          let breath = responses[2]
          if (breath.total > 0) {
            this.currentBF = Math.round(breath.data[breath.total - 1].value)
          }

          let steps = responses[3]
          if (steps.total > 0) {
            this.currentSR = Math.round(steps.data[steps.total - 1].value)
          }

          let chargeInfo = responses[4]
          if (chargeInfo.total > 0) {
            let lastChargeInfo = chargeInfo.data[chargeInfo.total - 1]
            this.currentDeviceCharge = lastChargeInfo.deviceCharge
            this.currentPhoneCharge = lastChargeInfo.phoneCharge
          }
        })
        .finally((this.load = false))
    },
    getPreviousRhytmogram() {
      return new Promise(resolve => {
        http
          .get('/ongoing-training/' + this.trainingId, {
            params: {
              type: 'hr'
            }
          })
          .then(response => {
            let rhythmogram = { timeValues: [], hrValues: [] }
            if (response.data.total > 0) {
              rhythmogram = response.data.data.reduce(
                function(obj, hrCurrent) {
                  obj.timeValues.push(parseInt(hrCurrent.time, 10))
                  obj.hrValues.push(parseInt(hrCurrent.value, 10))

                  return obj
                },
                { timeValues: [], hrValues: [] }
              )
              this.currentHR = rhythmogram.hrValues[rhythmogram.hrValues.length - 1]
              this.changeRangeHR()
            }
            return rhythmogram
          })
          .then(data => {
            resolve(data)
          })
      })
    },
    getPreviousEvents() {
      return new Promise(resolve => {
        http
          .get('/ongoing-training/' + this.trainingId, {
            params: {
              type: 'ce'
            }
          })
          .then(response => response.data)
          .then(data => {
            resolve(data)
          })
      })
    },
    getPreviousBreath() {
      return new Promise(resolve => {
        http
          .get('/ongoing-training/' + this.trainingId, {
            params: {
              type: 'bf'
            }
          })
          .then(response => response.data)
          .then(data => {
            resolve(data)
          })
      })
    },
    getPreviousSteps() {
      return new Promise(resolve => {
        http
          .get('/ongoing-training/' + this.trainingId, {
            params: {
              type: 'sr'
            }
          })
          .then(response => response.data)
          .then(data => {
            resolve(data)
          })
      })
    },
    getPreviousChargeInfo() {
      return new Promise(resolve => {
        http
          .get('/ongoing-training/' + this.trainingId, {
            params: {
              type: 'chrg'
            }
          })
          .then(response => response.data)
          .then(data => {
            resolve(data)
          })
      })
    },
    getCharge(chargeLevel) {
      if (!chargeLevel) return 'charge-middle'
      if (+chargeLevel <= 20) {
        return 'charge-low'
      } else if (+chargeLevel <= 60) {
        return 'charge-middle'
      } else return 'charge-hight'
    },
    changeRangeHR() {
      let rangeHR = document.getElementById(`rangeHR-${this.trainingId}`)
      rangeHR.value = this.currentHR
      let thumbClassList = 'default-thumb-color'
      if (this.hasRangeHR) {
        let colorLeft = `#E5E5E5`
        let colorRight = `#E5E5E5`
        if (this.currentHR <= this.training.heart_rate_min) {
          colorLeft = `#FAAD14`
          thumbClassList = 'min-thumb-color'
        } else if (this.currentHR >= this.training.heart_rate_max) {
          colorRight = `#F5222D`
          thumbClassList = 'max-thumb-color'
        }
        rangeHR.classList.remove('default-thumb-color', 'max-thumb-color', 'min-thumb-color')
        rangeHR.classList.add(thumbClassList)
        rangeHR.style.background = `linear-gradient(to right, ${colorLeft} ${this.training.heart_rate_min /
          2}%, #B2B4B6 ${this.training.heart_rate_min / 2}% ${this.training.heart_rate_max /
          2}%, ${colorRight} ${this.training.heart_rate_max / 2}%)`
      } else {
        rangeHR.classList.remove('default-thumb-color', 'max-thumb-color', 'min-thumb-color')
        rangeHR.classList.add(thumbClassList)
        rangeHR.style.background = `#B2B4B6`
      }
    },
    redrawEventsOnProgressBar() {
      this.eventCtx.clearRect(0, 0, this.eventCanvas.width, this.eventCanvas.height)
      this.eventsForTooltip = []

      this.events.forEach(ev => {
        this.createEventRange(ev)
      })
    },
    createAndSettingCanvases() {
      this.eventCanvas = document.getElementById(`event-${this.trainingId}`)
      this.eventCtx = this.eventCanvas.getContext('2d')
      this.eventCanvas.width = document.getElementById(`myProgress-${this.trainingId}`).clientWidth
      this.eventCanvas.height = 20

      this.tipCanvas = document.getElementById(`tip-${this.trainingId}`)
      this.tipCtx = this.tipCanvas.getContext('2d')
      this.tipCanvas.style.display = 'none'

      this.eventCanvas.addEventListener('mousemove', this.handleMouseMove)
      this.eventCanvas.addEventListener('mouseleave', this.focusout)
    },
    changeSessionDurationForProgressBar() {
      let currentDurationMinutes = this.now.diff(this.trainingStartDate, 'minutes')
      if (currentDurationMinutes + 1 > this.duration) {
        this.duration += DEFAULT_DURATION_MIN
        this.redrawEventsOnProgressBar()
      }
    },
    updateCurrentPositionOnProgressBar() {
      this.currentDuration = this.now.diff(this.trainingStartDate, 'seconds')
      let myBar = document.getElementById(`myBar-${this.trainingId}`)
      if (myBar) {
        let width = ((this.currentDuration / 60) * 100) / this.duration
        myBar.style.width = width + '%'
      }
    },
    checkConnectionStatus() {
      let diff = this.currentDuration - +this.lastDataTime
      if (diff >= 40) {
        this.connect = false
      } else this.connect = true
    },
    changeDataByEvent(event) {
      if (event.type === eventTypes.startTraining) {
        this.addTrainingToList(event.tr_id)
      }
      if (+this.trainingId !== +event.tr_id) {
        return
      }
      this.lastDataTime = event.type === 'SR' && event.value === 0 ? this.lastDataTime : event.time
      this.eventProcessing(event)
    },
    eventProcessing(event) {
      switch (event.type) {
        case eventTypes.changeHR:
          this.rhythmogram.timeValues.push(Math.round(event.time))
          this.rhythmogram.hrValues.push(Math.round(event.value))
          this.currentHR = event.value
          this.changeRangeHR()
          return
        case eventTypes.changeBF:
          this.currentBF = Math.round(event.value)
          return
        case eventTypes.changeSR:
          this.currentSR = event.value
          return
        case eventTypes.cardioEvent:
          this.addCardioEvent(event)
          return
        case eventTypes.devicesCharge:
          this.currentDeviceCharge = event.deviceCharge
          this.currentPhoneCharge = event.phoneCharge
          return
        case eventTypes.stopTraining:
          this.removeTrainingFromTheList(event.tr_id)
          return
        default:
          return
      }
    },
    addCardioEvent(event) {
      let time = timeString(Math.round(event.time), true)

      let eventObj = {
        isDuration: eventsConfig[event.episodeType].duration,
        episodeType: event.episodeType,
        value:
          event.episodeType === 'UserEvent'
            ? `${this.$t('sessionsInfo.heartRate')}: ${event.value}`
            : event.value,
        unit:
          event.episodeType === 'UserEvent' ? `${this.$t('unit.bpm')}` : eventsConfig[event.episodeType].unit,
        abbr: eventsConfig[event.episodeType].abbr,
        color: eventsConfig[event.episodeType].color,
        time: time
      }

      if (event.evt === 'B') {
        this.eventsBeginIndexes[event.episodeType] = this.events.length
        eventObj['beginTime'] = time
        eventObj['beginTimeInSec'] = Math.round(event.time)
        eventObj['endTime'] = time + 1
        this.createEventRange(eventObj)
      } else if (event.evt === 'E') {
        let idx = this.eventsBeginIndexes[event.episodeType]
        if (!this.events[idx]) {
          return
        }
        this.events[idx]['endTime'] = time
        eventObj['beginTimeInSec'] = this.events[idx]['beginTimeInSec']
        this.events[idx]['endTimeInSec'] = Math.round(event.time)
        eventObj['endTimeInSec'] = Math.round(event.time)
        this.createEventRange(eventObj)
        delete this.eventsBeginIndexes[event.episodeType]
        return
      } else {
        eventObj['beginTime'] = time
        eventObj['beginTimeInSec'] = Math.round(event.time)
        this.createEventRange(eventObj)
      }
      this.events.push(eventObj)
    },
    removeTrainingFromTheList(tr_id) {
      this.$emit('remove:trainingFromTheList', tr_id)
    },
    createEventRange(event) {
      let color = '#F5222D'
      if (event.color === 'warning') {
        color = '#FAAD14'
      } else if (event.color === 'secondary') {
        color = '#6c757d'
      } else if (event.color === 'primary') {
        color = '#0482dc'
      }
      let canvasWidth1percent = this.eventCanvas.clientWidth / 100
      let durationInSec1percent = (this.duration * 60) / 100
      let xStart = (event.beginTimeInSec * canvasWidth1percent) / durationInSec1percent
      let xEnd = event.endTimeInSec
        ? (event.endTimeInSec * canvasWidth1percent) / durationInSec1percent - xStart
        : 2
      let eventWithPosition = Object.assign(
        {
          xStart: Math.round(xStart),
          xEnd: Math.round(xStart + xEnd)
        },
        event
      )
      this.eventsForTooltip.push(eventWithPosition)
      this.eventCtx.globalAlpha = 1
      this.eventCtx.fillStyle = color
      this.eventCtx.beginPath()
      this.eventCtx.moveTo(xStart, 0)
      this.eventCtx.lineTo(xStart, this.eventCanvas.height)
      this.eventCtx.lineTo(xStart + xEnd, this.eventCanvas.height)
      this.eventCtx.lineTo(xStart + xEnd, 0)
      this.eventCtx.fill()
    },
    goToTraining() {
      this.$router.push({ name: 'session-view', params: { id: this.trainingId } })
    },
    setViewReport(view) {
      this.graphView = view
    },
    reloadEcg() {
      this.componentKey += 1
    },
    addTrainingToList(tr_id) {
      this.$emit('add:trainingToTheList', tr_id)
    },
    handleMouseMove(e) {
      e.preventDefault()
      e.stopPropagation()
      let mouseX = parseInt(e.offsetX)
      let event = this.eventsForTooltip.filter(el => {
        return mouseX >= el.xStart && mouseX <= el.xEnd
      })

      if (event.length > 0) {
        this.colorEvent = event[event.length - 1].color
        this.textEventAbbr = event[event.length - 1].abbr
        this.textEventTime = `${event[event.length - 1].time}`
        this.textEventValue = event[event.length - 1].value ? ` - ${event[event.length - 1].value}` : ''
        this.textEventValue += event[event.length - 1].unit ? event[event.length - 1].unit : ''
        if (event[event.length - 1].isDuration && event[event.length - 1].endTimeInSec) {
          let durationInSec = event[event.length - 1].endTimeInSec - event[event.length - 1].beginTimeInSec
          let str
          if (durationInSec > 60) {
            str = ` (${timeString(Math.round(durationInSec), true)})`
          } else str = ` (${durationInSec}${this.$tc('unit.sec', 1)})`
          this.textEventTime += str
        }
        this.mouseX = mouseX

        let maxString = Math.max(
          this.tipCtx.measureText(this.textEventAbbr).width,
          this.tipCtx.measureText(this.textEventTime).width - 17
        )
        document.getElementById(`eventTooltip-${this.trainingId}`).style.left =
          mouseX - (22 + (maxString - 20) / 2) + 'px'
        document.getElementById(`eventTooltip-${this.trainingId}`).style.display = 'block'
      } else {
        document.getElementById(`eventTooltip-${this.trainingId}`).style.display = 'none'
      }
    },
    focusout() {
      document.getElementById(`eventTooltip-${this.trainingId}`).style.display = 'none'
    }
  }
}
</script>
